import { useEffect, useRef } from 'react';

interface FloatingOrbsOverlayProps {
  colors?: {
    primary?: string;
    secondary?: string;
  };
  opacity?: number;
}

export default function FloatingOrbsOverlay({ colors, opacity = 0.3 }: FloatingOrbsOverlayProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId: number;
    const orbs: Orb[] = [];

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Parse CSS color string (hex or rgb/rgba) to [r,g,b]
    const parseCssColorToRgb = (input: string): [number, number, number] => {
      let c = input.trim();
      if (!c) return [10, 116, 255];
      const rgbMatch = c.match(/^rgba?\(([^)]+)\)$/i);
      if (rgbMatch) {
        const parts = rgbMatch[1].split(',').map(v => parseFloat(v.trim()));
        return [
          Math.max(0, Math.min(255, parts[0] || 0)),
          Math.max(0, Math.min(255, parts[1] || 0)),
          Math.max(0, Math.min(255, parts[2] || 0)),
        ];
      }
      if (c.startsWith('#')) c = c.slice(1);
      if (c.length === 3) c = c.split('').map(ch => ch + ch).join('');
      if (c.length !== 6) return [10, 116, 255];
      const r = parseInt(c.slice(0,2), 16);
      const g = parseInt(c.slice(2,4), 16);
      const b = parseInt(c.slice(4,6), 16);
      return [r,g,b];
    };

    // Accent color RGB, updated each frame from CSS variable
    let accentRgb: [number, number, number] = [10, 116, 255];

    class Orb {
      x: number;
      y: number;
      size: number;
      speedX: number;
      speedY: number;
      pulseSpeed: number;
      pulsePhase: number;
      isPrimary: boolean;

      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.size = Math.random() * 60 + 40;
        this.speedX = (Math.random() - 0.5) * 0.3;
        this.speedY = (Math.random() - 0.5) * 0.3;
        this.pulseSpeed = Math.random() * 0.02 + 0.01;
        this.pulsePhase = Math.random() * Math.PI * 2;
        this.isPrimary = Math.random() > 0.5;
      }

      update() {
        this.x += this.speedX;
        this.y += this.speedY;
        this.pulsePhase += this.pulseSpeed;

        if (this.x < -this.size || this.x > canvas.width + this.size) this.speedX *= -1;
        if (this.y < -this.size || this.y > canvas.height + this.size) this.speedY *= -1;
      }

      draw() {
        const pulse = Math.sin(this.pulsePhase) * 0.3 + 0.7;
        const currentSize = this.size * pulse;
        
        // Use provided colors if passed, otherwise theme accent for both
        let baseColor: string | null = null;
        if (colors?.primary || colors?.secondary) {
          baseColor = (this.isPrimary ? colors?.primary : colors?.secondary) || colors?.primary || colors?.secondary || null;
        }
        const [r, g, b] = baseColor ? parseCssColorToRgb(baseColor) : accentRgb;

        const gradient = ctx.createRadialGradient(
          this.x, this.y, 0,
          this.x, this.y, currentSize
        );
        
        gradient.addColorStop(0, `rgba(${r},${g},${b},0.38)`);
        gradient.addColorStop(0.5, `rgba(${r},${g},${b},0.19)`);
        gradient.addColorStop(1, `rgba(${r},${g},${b},0.0)`);

        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(this.x, this.y, currentSize, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    for (let i = 0; i < 8; i++) {
      orbs.push(new Orb());
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Read header accent each frame to respond to theme/accent changes
      const accent = getComputedStyle(document.documentElement)
        .getPropertyValue('--color-header-accent')
        .trim() || '#0A74FF';
      accentRgb = parseCssColorToRgb(accent);

      orbs.forEach(orb => {
        orb.update();
        orb.draw();
      });

      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      cancelAnimationFrame(animationFrameId);
    };
  }, [colors, opacity]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: -1,
        opacity: opacity
      }}
    />
  );
}
